import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import Discipline from '../components/Discipline';
import { mskToday } from '../lib/alerts';
import ReferralBoard from '../components/ReferralBoard';
import TasksTab from '../components/TasksTab';
import TaskBanners from '../components/TaskBanners';
import useTaskBadge from '../hooks/useTaskBadge';
import { hashTaskId } from '../lib/tasks';
import StaffPlanSummary from '../components/StaffPlanSummary';
import { Topbar, NavTabs, Card, SectionTitle, Loader, Btn } from '../components/UI';
import { supabase } from '../lib/supabase';
import { useRecruiting } from '../lib/recruiting';
import { rememberRoutes, canHideRoutes, hideRoute } from '../lib/data';
import HiddenRoutes from '../components/HiddenRoutes';
import { getActivePeriod, setStatus, clearStatus, setVacation, deleteVacation, closeVacancyByRoute, getPeriodDataForRoutes, getRouteWeekAgg, getRouteWeekAggTo, buildRouteWeekAggRaw, aggregateWeeks, getHyperDataForRoutes, aggregateHyper, getRowsByRouteChunks, getUnplannedVacations, getVacationsOverlappingPeriod, getRouteDayForecast } from '../lib/data';
import { exportTabellToExcel } from '../lib/exportExcel';
import EditableTabell from '../components/EditableTabell';
import DailyStatsPanel from '../components/DailyStatsPanel';
import { datesFor, weeksFor, DASH_CSS, KpiTiles, DiscTiles, HyperTiles, RouteMetricRow, HyperMetricRow, weeklySeries, weeklyHyperSeries, DynamicsChart, WeekStrip, ScopeTabs, TriggersCard } from '../components/DashParts';
import { downloadTmSummary, downloadForecastTm } from '../lib/summaryExport';
import ForecastPivot from '../components/ForecastPivot';
import HyperPivot from '../components/HyperPivot';
import NoPlanTree from '../components/NoPlanTree';
import AlertRibbon from '../components/AlertRibbon';
import SvManage, { isVacantSv } from '../components/SvManage';
import QualityPlanning from '../components/QualityPlanning';
import { routeNoEntry, buildNoForecastAlerts, buildRecruitAlerts, buildAlerts, buildPlanAlerts, sortAlerts, partialInfo, mergePartial } from '../lib/alerts';

function tmPointGroups(routes, hdata) {
  const byShip = {};
  routes.forEach(r => (hdata.pointsByRoute?.[r.id] || []).forEach(p => {
    const key = String(p.ship_to);
    if (!byShip[key]) byShip[key] = { ship_to: p.ship_to, client: p.client, address: p.address, pointIds: [] };
    byShip[key].pointIds.push(p.id);
    if (p.client && !byShip[key].client) byShip[key].client = p.client;
    if (p.address && !byShip[key].address) byShip[key].address = p.address;
  }));
  return Object.values(byShip);
}
function buildTmHyperRoots(svGroups, hdata) {
  return svGroups.map(g => {
    const groups = tmPointGroups(g.routes, hdata);
    const children = groups.map(gr => ({ key: `hp:${g.sv.id}:${gr.ship_to}`, label: gr.client || '—', sub: String(gr.ship_to), addr: gr.address || null, pointKey: `${g.sv.id}:${gr.ship_to}`, pointIds: gr.pointIds }));
    return { key: `hsv:${g.sv.id}`, label: g.sv.full_name, pointIds: children.flatMap(c => c.pointIds), children };
  }).filter(n => n.children.length);
}
function tmHyperUnplanned(roots, weeks, weekNums, daily) {
  const dates = weekNums.flatMap(w => (weeks[w]?.days || []).map(d => d.date));
  const out = new Set();
  const walk = node => {
    if (node.pointKey) {
      let ord = 0, plan = 0;
      node.pointIds.forEach(pid => dates.forEach(dt => { const c = daily[pid]?.[dt]; if (!c) return; ord += c.order || 0; if (c.plan != null) plan += c.plan; }));
      if (ord > 0 && plan === 0) out.add(node.pointKey);
    }
    (node.children || []).forEach(walk);
  };
  roots.forEach(walk);
  return out;
}

function mrcNameTm(r) { const m = r.merchandisers; const x = Array.isArray(m) ? m[0] : m; return (r.is_vacancy || !x?.full_name) ? `Вакансия (${r.internal_code})` : x.full_name; }
function buildTmForecastRoots(svGroups) {
  return svGroups.map(g => ({
    key: `sv:${g.sv.id}`, label: g.sv.full_name, routes: g.routes,
    children: g.routes.map(r => ({ key: `rt:${r.id}`, label: mrcNameTm(r), sub: [r.internal_code, r.external_code].filter(Boolean).join(' · '), routes: [r], routeId: r.id })),
  }));
}

// Merchandisers может прийти как массив или как объект (зависит от unique constraint в БД)
function getMrc(route) {
  const m = route.merchandisers;
  if (!m) return null;
  return Array.isArray(m) ? m[0] : m;
}

const WEEK_COLORS = {
  36: { hdr: '#2E75B6', light: '#D6E4F0' },
  37: { hdr: '#70AD47', light: '#E2EFDA' },
  38: { hdr: '#FFC000', light: '#FFF2CC' },
  39: { hdr: '#F4B183', light: '#FCE4D6' },
  40: { hdr: '#808080', light: '#EDEDED' },
};

// #4 — накопительные алёрты по СВ для ТМ: отпуска / планирование / SL (оба сигнала).
function computeSvAlerts({ routes, rweeks, weekNums, pendingCount, hasOverlap, otCount }) {
  const out = [];
  // Отпуска
  if (pendingCount > 0 || hasOverlap || otCount > 0) {
    const parts = [];
    if (hasOverlap) parts.push('пересечение');
    if (pendingCount > 0) parts.push(`${pendingCount} на подтв.`);
    if (otCount > 0) parts.push(`ОТ без плана: ${otCount} дн.`);
    out.push({ cat: 'Отпуска', level: (hasOverlap || otCount > 0) ? 'bad' : 'warn', text: parts.join(' · ') });
  }
  // Суммы по неделям
  const wk = weekNums.map(w => {
    let ord = 0, plan = 0, fact = 0, hasF = false;
    routes.forEach(r => { const c = rweeks[r.id]?.[w]; if (!c) return; ord += c.ord || 0; plan += c.plan || 0; if (c.fact != null) { fact += c.fact; hasF = true; } });
    return { w, ord, plan, fact, hasF, sl: (ord > 0 && hasF) ? fact / ord * 100 : null, planPct: ord > 0 ? plan / ord * 100 : null };
  });
  // SL — ниже порога ИЛИ падение к прошлой неделе
  const withSl = wk.filter(x => x.sl != null);
  const last = withSl[withSl.length - 1];
  const prev = withSl[withSl.length - 2];
  if (last) {
    if (last.sl < 95) out.push({ cat: 'SL', level: last.sl < 90 ? 'bad' : 'warn', text: `${Math.round(last.sl)}% (W${last.w})` });
    else if (prev && last.sl < prev.sl - 0.5) out.push({ cat: 'SL', level: 'warn', text: `падение ${Math.round(prev.sl)}→${Math.round(last.sl)}% (W${last.w})` });
  }
  // Планирование — план/заказ ниже нормы ИЛИ прогноз≠факт сверх порога
  const lastOrd = wk.filter(x => x.ord > 0).slice(-1)[0];
  if (lastOrd) {
    if (lastOrd.planPct != null && lastOrd.planPct < 95) {
      out.push({ cat: 'Планирование', level: lastOrd.planPct < 85 ? 'bad' : 'warn', text: `план/заказ ${Math.round(lastOrd.planPct)}% (W${lastOrd.w})` });
    } else if (lastOrd.hasF) {
      const dev = Math.abs(lastOrd.fact - lastOrd.plan) / Math.max(1, lastOrd.ord) * 100;
      if (dev > 12) out.push({ cat: 'Планирование', level: dev > 20 ? 'bad' : 'warn', text: `прогноз≠факт ${Math.round(dev)}% (W${lastOrd.w})` });
    }
  }
  return out;
}

export default function TmPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState(() => hashTaskId() ? 6 : 0);
  const [taskN, refreshTaskN] = useTaskBadge(user.id);
  const [period, setPeriod] = useState(null);
  const [svGroups, setSvGroups] = useState([]); // [{ sv, routes, statuses }]
  const [vacations, setVacations] = useState([]);
  const [servicePct, setServicePct] = useState({}); // { route_id: { week_number: pct } }
  const [personalForecasts, setPersonalForecasts] = useState({}); // { sv_id: { week_number: pct } }
  const [visitFacts, setVisitFacts] = useState([]);
  const [rweeks, setRweeks] = useState({}); // свёртка маршрут×неделя
  const [hdata, setHdata] = useState({ pointsByRoute: {}, daily: {} }); // гипперы
  const [otAlert, setOtAlert] = useState({ byRoute: {}, total: 0, routes: 0 }); // триггер ОТ без плана
  const [selectedSvId, setSelectedSvId] = useState(null); // drill-down
  const [groupFilter, setGroupFilter] = useState('all'); // #1 — выбор команды СВ на «Моя группа»
  const [dashPeriodSel, setDashPeriodSel] = useState('month'); // #3 — сохранять выбор недели дашборда между вкладками
  const [openGroups, setOpenGroups] = useState({}); // Батч3 — раскрытые команды СВ (по умолчанию схлопнуты)
  const [focusRouteId, setFocusRouteId] = useState(null); // переход из алёрта «нет планирования» в сводную прогноза
  const [fcMode, setFcMode] = useState('routes');
  const [focusHyperKey, setFocusHyperKey] = useState(null);
  const frozenMode = false; // заморозка убрана
  const [rweeksFrozen, setRweeksFrozen] = useState({});
  const [partialQ, setPartialQ] = useState(null); // идущая неделя «на вчера»: { week, until, frozen, live }
  const [hyperFrozenDaily, setHyperFrozenDaily] = useState({});
  const [freezeAt, setFreezeAt] = useState(null);
  const [lastSeen, setLastSeen] = useState({}); // { users.id: ISO } — последний вход СВ
  const [navFocus, setNavFocus] = useState(null); // переход из алёрта: { tab, routeId, routeIds, date, key }
  const [loading, setLoading]   = useState(true);
  const recruiting = useRecruiting(); // заявки подбора для алёртов
  const [weeks, setWeeks]       = useState({});
  const [weekNums, setWeekNums] = useState([]);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      setLoading(true);
      const p = await getActivePeriod();
      setPeriod(p);

      // Недели
      const wks = {};
      (p.work_weeks || []).forEach(w => {
        const days = [];
        const from = new Date(w.date_from), to = new Date(w.date_to);
        for (let d = new Date(from); d <= to; d.setDate(d.getDate()+1)) {
          days.push({ day: d.getDate(), dow: d.getDay(), date: d.toISOString().split('T')[0] });
        }
        wks[w.week_number] = { ...w, days };
      });
      setWeeks(wks);
      const wn = Object.keys(wks).map(Number).sort((a,b)=>a-b);
      setWeekNums(wn);

      // СВ этого ТМ
      const { data: svList } = await supabase
        .from('supervisors')
        .select('id, full_name, internal_code, user_id')
        .eq('tm_id', user.id)
        .eq('is_active', true);

      if (!svList?.length) { setLoading(false); return; }

      // Для каждого СВ — маршруты и статусы
      const groups = await Promise.all(svList.map(async sv => {
        const { data: routes } = await supabase
          .from('routes')
          .select('*, merchandisers(*)')
          .eq('sv_id', sv.id)
          .eq('is_active', true)
          .neq('is_hidden', true);

        const allR = routes || [];
        const routeIds = allR.filter(r => !r.is_pm).map(r => r.id);
        let statusMap = {};
        if (routeIds.length) {
          const statusList = await getRowsByRouteChunks('daily_statuses', 'route_id, work_date, status_code', p.id, routeIds);
          (statusList || []).forEach(s => {
            if (!statusMap[s.route_id]) statusMap[s.route_id] = {};
            statusMap[s.route_id][s.work_date] = s.status_code;
          });
        }

        return { sv, routes: allR.filter(r => !r.is_pm), pmRoutes: allR.filter(r => r.is_pm), statuses: statusMap };
      }));

      groups.forEach(g => rememberRoutes(g.routes));
      setSvGroups(groups);

      // Последний вход СВ (поле может быть ещё не создано — тогда просто нет алёрта)
      try {
        const uids = svList.map(s => s.user_id).filter(Boolean);
        if (uids.length) {
          const { data: us, error: ue } = await supabase.from('users').select('id, last_seen_at').in('id', uids);
          if (!ue) { const m = {}; (us || []).forEach(u => { if (u.last_seen_at) m[u.id] = u.last_seen_at; }); setLastSeen(m); }
        }
      } catch (e) { /* ignore */ }

      // Отпуска по всем маршрутам группы
      const allRouteIds = groups.flatMap(g => g.routes.map(r => r.id));
      if (allRouteIds.length) {
        const vacs = await getVacationsOverlappingPeriod(p.id, [...allRouteIds, ...groups.flatMap(g => (g.pmRoutes || []).map(r => r.id))]);
        setVacations(vacs || []);

        // Триггер: ОТ в табеле без подтверждённого отпуска
        const otu = await getUnplannedVacations(p.id, allRouteIds, vacs || []);
        setOtAlert(otu);

        // Service%
        const svcData = await getRowsByRouteChunks('service_pct', 'route_id, week_number, service_pct', p.id, allRouteIds);
        const svcMap = {};
        (svcData || []).forEach(s => {
          if (!svcMap[s.route_id]) svcMap[s.route_id] = {};
          svcMap[s.route_id][s.week_number] = s.service_pct;
        });
        setServicePct(svcMap);
      }

      // Личные прогнозы СВ
      const svIds = groups.map(g => g.sv.id);
      if (svIds.length) {
        const { getSvPersonalForecastsBySv } = await import('../lib/data');
        const forecasts = await getSvPersonalForecastsBySv(p.id, svIds);
        const fMap = {};
        forecasts.forEach(f => {
          if (!fMap[f.supervisor_id]) fMap[f.supervisor_id] = {};
          fMap[f.supervisor_id][f.week_number] = f.forecast_pct;
        });
        setPersonalForecasts(fMap);
      }

      // Факты визитов (для сверки)
      if (allRouteIds.length) {
        const { getVisitFactsByRoutes } = await import('../lib/data');
        const facts = await getVisitFactsByRoutes(p.id, allRouteIds);
        setVisitFacts(facts);

        // Свёртка маршрут×неделя из БД (оптимизировано). Фолбэк — сырые данные.
        const allRoutes = groups.flatMap(g => g.routes);
        let rw;
        try {
          rw = await getRouteWeekAgg(p.id, false, allRouteIds);
          if (!rw || !Object.keys(rw).length) throw new Error('empty rpc');
        } catch (e) {
          console.warn('[ТМ] RPC dash_route_week недоступна, фолбэк на сырые данные:', e?.message || e);
          const pd = await getPeriodDataForRoutes(p.id, allRouteIds);
          rw = buildRouteWeekAggRaw(allRoutes, wks, wn, pd);
        }
        setRweeks(rw);

        // Гипперы группы
        const hd = await getHyperDataForRoutes(p.id, allRouteIds);
        setHdata(hd);

        // Заморозка прогноза убрана: везде живые данные.
        try {
          const pi = partialInfo(wks, wn);
          if (pi) {
            const pl = await getRouteWeekAggTo(p.id, false, allRouteIds, pi.until);
            setPartialQ({ ...pi, frozen: pl, live: pl });
          }
        } catch (e) { console.warn('partial week', e?.message || e); }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function calcMetrics(statuses, routeId, days) {
    let exits=0, sick=0, vacation=0, dayoff=0, vacancy=0, overlaps=0;
    const OV = new Set(['БЛ+МР','БЛ+ПМ','БЛ+ВЫХ','ОТ+МР','ОТ+ПМ','ОТ+ВЫХ','ДО+МР','ДО+ПМ','ДО+ВЫХ','ВК+МР','ВК+ПМ','ВК+ВЫХ']);
    days.forEach(({ date }) => {
      const s = statuses[routeId]?.[date];
      if (!s) return;
      if (s==='1'||s==='СВ') exits++;
      if (s==='БЛ-') sick++;
      if (s==='ОТ-') vacation++;
      if (s==='ДО-') dayoff++;
      if (s==='ВК-') vacancy++;
      if (OV.has(s)) overlaps++;
    });
    return { exits, sick, vacation, dayoff, vacancy, overlaps };
  }

  // Алёрты ТМ (отпуска, покрытие, данные СВ, вакансии, планирование)
  const tmAlerts = sortAlerts([
    ...buildAlerts({ svGroups, vacations, weeks, weekNums, lastSeen, tmId: user.id, tmName: user.full_name }),
    ...buildPlanAlerts({ svGroups, weeks, weekNums, rwFrozen: rweeks, rwLive: rweeks, forecasts: personalForecasts, tmId: user.id, tmName: user.full_name }),
    ...buildNoForecastAlerts({ svGroups, weeks, weekNums, rweeks, vacations, tmId: user.id, tmName: user.full_name }),
    ...buildRecruitAlerts({ svGroups, recruiting, tmId: user.id, tmName: user.full_name }),
  ]);
  // «Перейти»: открыть нужного СВ и подсветить неделю / маршрут / отпуск
  function goToAlert(a) {
    const g = a.goto || {};
    if (g.top === 'forecast') { setTab(3); return; }
    setTab(0);
    setGroupFilter('all');
    setSelectedSvId(g.svId);
    setNavFocus({ ...g, key: Date.now() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  if (loading) return <Loader text="Загружаем группу..." />;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Topbar
        title={user.full_name}
        subtitle="Территориальный менеджер"
        rightContent={
          <button onClick={logout} style={{ background: 'rgba(255,255,255,.12)', border: 'none', color: 'white', padding: '6px 12px', borderRadius: 6, fontSize: 12 }}>Выйти</button>
        }
      />
      <NavTabs tabs={['Моя группа', 'Итоги', 'Динамика', 'Прогноз', 'Дисциплина СВ', 'Приведи друга', { label: 'Мои задачи', badge: taskN }]} active={tab} onChange={setTab} />

      <div style={{ padding: 16, maxWidth: 700, margin: '0 auto', width: '100%' }}>
        {tab !== 6 && <TaskBanners userId={user.id} refreshKey={taskN} onOpen={() => setTab(6)} />}
        {tab === 6 && <TasksTab user={user} onChanged={refreshTaskN} />}

        {tab === 0 && (
          <>
            <SectionTitle>{period?.name}</SectionTitle>
            {!selectedSvId && (
              <AlertRibbon alerts={tmAlerts.filter(x => x.cat !== 'plan')} onGo={goToAlert} />
            )}

            {selectedSvId ? (
              <SvDrillDown
                svId={selectedSvId}
                svGroups={svGroups}
                vacations={vacations}
                period={period}
                weeks={weeks}
                weekNums={weekNums}
                onBack={() => { setSelectedSvId(null); setNavFocus(null); }}
                focus={navFocus}
                onUpdateGroups={setSvGroups}
                onUpdateVacations={setVacations}
                tmUserId={user.id}
                visitFacts={visitFacts}
              />
            ) : (
            <>
            {/* #1 — выпадающий выбор команды СВ */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Команда СВ:</span>
              <select value={groupFilter} onChange={e => setGroupFilter(e.target.value)}
                style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, background: 'white', fontWeight: 600, color: 'var(--text)' }}>
                <option value="all">Все команды ({svGroups.length})</option>
                {svGroups.map(g => (
                  <option key={g.sv.id} value={g.sv.id}>{g.sv.full_name} · {g.routes.length} марш.</option>
                ))}
              </select>
            </div>
            {svGroups.filter(g => groupFilter === 'all' || g.sv.id === groupFilter).map(({ sv, routes, statuses }) => {
              const routeIds = routes.map(r => r.id);
              const pendingCount = vacations.filter(v => v.status === 'pending' && routeIds.includes(v.route_id)).length;

              // Пересечения отпусков внутри команды этого СВ (только approved)
              const svVacs = vacations.filter(v => v.status === 'approved' && routeIds.includes(v.route_id));
              const overlapDayCounts = {};
              svVacs.forEach(v => {
                const from = new Date(v.date_from), to = new Date(v.date_to);
                for (let d = new Date(from); d <= to; d.setDate(d.getDate()+1)) {
                  const key = d.toISOString().split('T')[0];
                  overlapDayCounts[key] = (overlapDayCounts[key] || 0) + 1;
                }
              });
              const hasOverlap = Object.values(overlapDayCounts).some(c => c >= 2);

              return (
                <Card key={sv.id}>
                  <div style={{ padding: '10px 16px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div onClick={() => setOpenGroups(g => ({ ...g, [sv.id]: !(g[sv.id] || groupFilter === sv.id) }))}
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 240px', minWidth: 0 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)', transition: 'transform .15s', transform: (openGroups[sv.id] || groupFilter === sv.id) ? 'rotate(90deg)' : 'none' }}>▶</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{sv.full_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{routes.length} маршрутов · {(openGroups[sv.id] || groupFilter === sv.id) ? 'свернуть' : 'развернуть'}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-start', flex: '0 1 auto' }}>
                      {(() => {
                        const otCount = routeIds.reduce((s, id) => s + (otAlert.byRoute?.[id] || 0), 0);
                        const alerts = computeSvAlerts({ routes, rweeks, weekNums, pendingCount, hasOverlap, otCount });
                        const mine = tmAlerts.filter(x => x.svId === sv.id && ['data', 'vacancy', 'cover', 'tabel', 'nofc'].includes(x.cat));
                        const newChips = ['tabel', 'nofc', 'data', 'cover', 'vacancy'].map(cat => {
                          const list = mine.filter(x => x.cat === cat);
                          if (!list.length) return null;
                          const label = cat === 'tabel' ? 'Табель · нет статусов вчера'
                            : cat === 'nofc' ? `Нет прогноза · ${list[0].title.replace(/^Нет прогноза: /, '').replace(' маршрутов', '')}`
                            : cat === 'data' ? `Данные · ${list.length > 1 ? list.length : list[0].title.replace('Не заходил в систему', 'не заходил')}`
                            : cat === 'cover' ? `Покрытие · ${list.length}` : `Вакансии · ${list.length}`;
                          const bad = list.some(x => x.level === 'bad');
                          return (
                            <span key={cat} onClick={() => goToAlert(list[0])} title="Перейти"
                              style={{ cursor: 'pointer', fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: bad ? 'var(--red-bg)' : '#FFF7E0', color: bad ? 'var(--red)' : '#8A6100', border: bad ? 'none' : '1px solid #F0C56A' }}>
                              {label}
                            </span>
                          );
                        }).filter(Boolean);
                        if (!alerts.length && !newChips.length) return <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600 }}>✓ без алёртов</span>;
                        return [...newChips, ...alerts.map((a, i) => (
                          <span key={i} title={`${a.cat}: ${a.text}`}
                            style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: a.level === 'bad' ? 'var(--red-bg)' : '#FFF7E0', color: a.level === 'bad' ? 'var(--red)' : '#8A6100', border: a.level === 'bad' ? 'none' : '1px solid #F0C56A' }}>
                            {a.cat === 'Отпуска' ? '🏖' : a.cat === 'SL' ? 'SL' : 'План'} {a.text}
                          </span>
                        ))];
                      })()}
                      <Btn size="sm" onClick={() => setSelectedSvId(sv.id)}>✏️ Открыть</Btn>
                    </div>
                  </div>


                  {(openGroups[sv.id] || groupFilter === sv.id) && routes.map(route => {
                    const mrc = getMrc(route);
                    const monthMetrics = weekNums.reduce((acc, w) => {
                      const m = calcMetrics(statuses, route.id, weeks[w]?.days||[]);
                      acc.exits += m.exits; acc.sick += m.sick; acc.vacation += m.vacation;
                      return acc;
                    }, { exits: 0, sick: 0, vacation: 0 });

                    return (
                      <div key={route.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: monthMetrics.sick > 0 ? 'var(--red)' : monthMetrics.vacation > 0 ? 'var(--yellow)' : 'var(--text)' }}>
                            {mrc?.full_name || 'Вакансия'}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{route.internal_code}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {weekNums.map(w => {
                            const m = calcMetrics(statuses, route.id, weeks[w]?.days||[]);
                            const c = WEEK_COLORS[w] || WEEK_COLORS[36];
                            return (
                              <div key={w} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4px 6px', borderRadius: 6, background: c.light, minWidth: 36 }}>
                                <span style={{ fontSize: 9, fontWeight: 700, color: c.hdr }}>W{w}</span>
                                <span style={{ fontSize: 15, fontWeight: 700 }}>{m.exits}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </Card>
              );
            })}
            </>
            )}
          </>
        )}

        {tab === 5 && <ReferralBoard mode="tm" userId={user.id} />}
        {tab === 1 && <TmDashboard svGroups={svGroups} weeks={weeks} weekNums={weekNums} rweeks={rweeks} hdata={hdata} otAlert={otAlert} tmName={user.full_name} periodSel={dashPeriodSel} setPeriodSel={setDashPeriodSel} alerts={tmAlerts} onGo={goToAlert} />}
        {tab === 4 && (
          <Discipline period={period} weeks={period?.work_weeks || []}
            units={svGroups.filter(g => !isVacantSv(g.sv)).map(g => ({ id: g.sv.id, label: g.sv.full_name, sub: `${g.routes.length} маршрутов`, svIds: [g.sv.id] }))} />
        )}
        {tab === 2 && <TmDynamicsTab svGroups={svGroups} weeks={weeks} weekNums={weekNums} calcMetrics={calcMetrics} vacations={vacations} />}
        {tab === 3 && (() => {
          const rweeksUse = rweeks;
          const hyperDailyUse = hdata.daily || {};
          const roots = buildTmForecastRoots(svGroups);
          const todayStr = mskToday();
          const curWeek = weekNums.find(w => weeks[w] && weeks[w].date_from <= todayStr && todayStr <= weeks[w].date_to) ?? null;
          const unplanned = new Set();
          svGroups.forEach(g => g.routes.forEach(r => {
            if (r.is_vacancy) return;
            let plan = 0, ord = 0;
            weekNums.forEach(w => { const c = rweeksUse[r.id]?.[w]; if (c) { plan += c.plan || 0; ord += c.ord || 0; } });
            const cw = curWeek != null ? rweeksUse[r.id]?.[curWeek] : null;
            const curZero = !!cw && (cw.ord || 0) > 0 && (cw.plan || 0) === 0;
            const noEntry = ord > 0 && !isVacantSv(g.sv) && Object.keys(g.statuses?.[r.id] || {}).length === 0 && !vacations.some(v => v.route_id === r.id);
            if ((ord > 0 && plan === 0) || curZero || noEntry) unplanned.add(r.id);
          }));
          const hRoots = buildTmHyperRoots(svGroups, hdata);
          const hUnplanned = tmHyperUnplanned(hRoots, weeks, weekNums, hyperDailyUse);
          return (
          <>
            <QualityPlanning
              units={svGroups.map(g => ({ id: g.sv.id, label: g.sv.full_name, sub: `${g.routes.length} маршрутов`, groups: [g] }))}
              unitTitle="СВ" weeks={weeks} weekNums={weekNums}
              rwFrozen={partialQ ? mergePartial(rweeks, partialQ.live, partialQ.week) : rweeks}
              rwLive={partialQ ? mergePartial(rweeks, partialQ.live, partialQ.week) : rweeks}
              partial={partialQ} forecasts={personalForecasts} 
              onGo={(svId) => goToAlert({ goto: { svId, tab: 'tabel' } })} />
            <SectionTitle>Планирование кадров</SectionTitle>
            <StaffPlanSummary mode="tm" tmId={user.id} />
            <SectionTitle>Прогноз — сводная</SectionTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
              <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                {[['routes', 'Маршруты'], ['hyper', 'Гипперы']].map(([m, l]) => (
                  <button key={m} onClick={() => setFcMode(m)} style={{ border: 0, background: fcMode === m ? 'var(--accent)' : 'white', color: fcMode === m ? 'white' : 'var(--text-muted)', fontSize: 12, fontWeight: 600, padding: '7px 16px', cursor: 'pointer' }}>{l}</button>
                ))}
              </div>
            </div>
            {fcMode === 'routes' ? (
              <>
                {unplanned.size > 0 && <NoPlanTree roots={roots} unplannedSet={unplanned} onPick={setFocusRouteId} leafId={(n) => n.routeId ?? null} unit="маршр." />}
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>СВ → Маршрут. Ячейка = Прогноз/Заказ + %. Клик по маршруту — прогноз по дням. Живые данные.</div>
                <ForecastPivot roots={roots} weekNums={weekNums} rweeks={rweeksUse} weeks={weeks} srcKey={frozenMode}
                  loadRouteDays={(routeId) => getRouteDayForecast(period.id, routeId, frozenMode)} focusRouteId={focusRouteId} />
              </>
            ) : (
              <>
                {hUnplanned.size > 0 && <NoPlanTree roots={hRoots} unplannedSet={hUnplanned} onPick={setFocusHyperKey} leafId={(n) => n.pointKey ?? null} unit="точек" />}
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>СВ → Точка (ship_to). Ячейка = Планир./Заказ + %. Клик по точке — планирование по дням. Живые данные.</div>
                <HyperPivot roots={hRoots} weekNums={weekNums} weeks={weeks} daily={hyperDailyUse} focusKey={focusHyperKey} />
              </>
            )}
          </>
          );
        })()}
      </div>
    </div>
  );
}

// ── ДАШБОРД ТМ: Показатели / Дисциплина (Заказ/Прогноз/Факт, SL, дисциплина) ──
function TmDashboard({ svGroups, weeks, weekNums, rweeks, hdata, otAlert, tmName, periodSel, setPeriodSel, alerts = [], onGo }) {
  const [view, setView] = useState('kpi');
  const [openSv, setOpenSv] = useState(null);
  const [svSel, setSvSel] = useState('all');   // срез графика по СВ
  const dates = datesFor(periodSel, weeks, weekNums);       // для гиппера
  const selWeeks = weeksFor(periodSel, weekNums);           // для показателей/дисциплины
  const isH = view === 'hyper';
  const Tiles = view === 'kpi' ? KpiTiles : view === 'disc' ? DiscTiles : HyperTiles;
  const agg = (routes) => isH ? aggregateHyper(routes, dates, hdata) : aggregateWeeks(routes, selWeeks, rweeks);

  const allRoutes = svGroups.flatMap(g => g.routes);
  const teamAgg = agg(allRoutes);
  // График/полоса недель перестраиваются под выбранного СВ (табы над графиком)
  const scopedSv = svSel !== 'all' ? svGroups.find(g => g.sv.id === svSel) : null;
  const chartRoutes = scopedSv ? scopedSv.routes : allRoutes;
  const chartLabel = scopedSv ? scopedSv.sv.full_name : 'вся группа';
  const activeRoutes = chartRoutes.filter(r => !r.is_vacancy).length;
  const wser = isH ? weeklyHyperSeries(chartRoutes, weeks, weekNums, hdata) : weeklySeries(chartRoutes, weekNums, rweeks);
  const chartSeries = view === 'kpi'
    ? [{ key: 'sl', name: 'SL Факт/Заказ', color: '#2a78d6' }, { key: 'pq', name: 'Прогноз/Заказ', color: '#1baf7a' }, { key: 'pf', name: 'Прогноз/Факт', color: '#eb6834' }]
    : view === 'hyper'
    ? [{ key: 'sl', name: 'SL Факт/Заказ', color: '#2a78d6' }, { key: 'pq', name: 'План/Заказ', color: '#1baf7a' }, { key: 'pf', name: 'План/Факт', color: '#eb6834' }]
    : [{ key: 'overlaps', name: 'Перекрытия', color: '#2a78d6' }, { key: 'sick', name: 'БЛ непокр', color: '#e34948' }, { key: 'vac', name: 'ОТ непокр', color: '#eda100' }, { key: 'vacRoutes', name: 'Вакансии', color: '#4a3aa7' }];
  const chartRefs = (view === 'kpi' || isH) ? [{ y: 100, label: '100%' }, { y: 95, label: 'SL 95%' }] : [];

  return (
    <div>
      <style>{DASH_CSS}</style>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div className={'dseg' + (view === 'disc' ? ' disc' : view === 'hyper' ? ' hyp' : '')}>
          <button className={view === 'kpi' ? 'on' : ''} onClick={() => setView('kpi')}>Показатели</button>
          <button className={view === 'disc' ? 'on' : ''} onClick={() => setView('disc')}>Дисциплина</button>
          <button className={view === 'hyper' ? 'on' : ''} onClick={() => setView('hyper')}>Гипперы</button>
        </div>
        <div className="dseg">
          <button className={periodSel === 'month' ? 'on' : ''} onClick={() => setPeriodSel('month')}>Месяц</button>
          {weekNums.map(w => <button key={w} className={periodSel === w ? 'on' : ''} onClick={() => setPeriodSel(w)}>W{w}</button>)}
        </div>
        <button onClick={() => downloadForecastTm(svGroups, tmName, weekNums, rweeks)}
          style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, border: 0, background: 'var(--accent)', color: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700 }}>
          ⬇ Прогноз по СВ
        </button>
        <button onClick={() => downloadTmSummary(svGroups, tmName, weeks, weekNums, rweeks)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600 }}>
          ⬇ Скачать Excel
        </button>
      </div>

      <SectionTitle>Вся группа</SectionTitle>
      <Card style={{ marginBottom: 12 }}>
        <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 13, minWidth: 90 }}>Итого · {svGroups.length} СВ</div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}><Tiles a={teamAgg} /></div>
        </div>
      </Card>

      <TriggersCard items={(() => {
        const t = allRoutes.reduce((s, r) => s + (otAlert?.byRoute?.[r.id] || 0), 0);
        const rc = allRoutes.reduce((s, r) => s + (otAlert?.byRoute?.[r.id] ? 1 : 0), 0);
        const items = [];
        if (t > 0) {
          const g0 = svGroups.find(g => g.routes.some(r => otAlert?.byRoute?.[r.id]));
          const r0 = g0?.routes.find(r => otAlert?.byRoute?.[r.id]);
          items.push({ level: 'warn', title: 'ОТ без подтверждённого отпуска', detail: `${t} дн. по ${rc} маршрутам — в табеле стоит отпуск, но он не запланирован/не подтверждён`,
            go: (g0 && onGo) ? () => onGo({ goto: { svId: g0.sv.id, tab: 'tabel', routeId: r0?.id } }) : null });
        }
        // Вакансии дольше N дней (по одному маршруту — своя строка) и точность планирования
        alerts.filter(a => a.cat === 'vacancy' || a.cat === 'plan').forEach(a => items.push({ level: a.level, title: a.title, detail: a.detail, go: onGo ? () => onGo(a) : null }));
        return items;
      })()} />

      <Card style={{ marginBottom: 12 }}>
        <div style={{ padding: '12px 14px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 10 }}>
            Динамика по неделям · {view === 'kpi' ? 'показатели' : view === 'hyper' ? 'гипперы' : 'дисциплина'}
          </div>
          <ScopeTabs label="СВ" value={svSel} onPick={setSvSel}
                     items={[{ id: 'all', label: 'Вся группа' }, ...svGroups.map(g => ({ id: g.sv.id, label: g.sv.full_name }))]} />
          <DynamicsChart data={wser} series={chartSeries} unit={(view === 'kpi' || isH) ? '%' : ''} refs={chartRefs} />
          <div style={{ marginTop: 12 }}><WeekStrip data={wser} view={view} activeRoutes={activeRoutes} /></div>
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
            Показан: <b style={{ color: 'var(--text)' }}>{chartLabel}</b> · {scopedSv
              ? <>{activeRoutes} рабочих маршрутов{chartRoutes.length - activeRoutes > 0 && <span> · {chartRoutes.length - activeRoutes} вакансий</span>}</>
              : <>{svGroups.length} СВ · {activeRoutes} рабочих маршрутов{allRoutes.length - activeRoutes > 0 && <span> · {allRoutes.length - activeRoutes} вакансий</span>}</>}
          </div>
        </div>
      </Card>

      <SectionTitle>По каждому СВ</SectionTitle>
      {svGroups.map(({ sv, routes }) => {
        const a = agg(routes);
        const isOpen = openSv === sv.id;
        return (
          <Card key={sv.id} style={{ marginBottom: 8 }}>
            <div onClick={() => setOpenSv(isOpen ? null : sv.id)} style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', cursor: 'pointer', background: 'var(--surface2)' }}>
              <div><div style={{ fontWeight: 600, fontSize: 13 }}>{sv.full_name}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{routes.length} маршрутов</div></div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}><Tiles a={a} /><span style={{ color: 'var(--text-muted)', transform: isOpen ? 'rotate(90deg)' : 'none' }}>▶</span></div>
            </div>
            {isOpen && (
              <div style={{ padding: '4px 14px 10px' }}>
                {isH
                  ? routes.map(route => {
                      const ra = aggregateHyper([route], dates, hdata);
                      if (!ra.points) return null;
                      const mrc = getMrc(route);
                      const name = mrc?.full_name || route.internal_code;
                      return <HyperMetricRow key={route.id} name={name} a={ra} />;
                    })
                  : routes.map(route => {
                      const m = aggregateWeeks([route], selWeeks, rweeks);
                      const mrc = getMrc(route);
                      const name = route.is_vacancy ? `Вакансия (${route.internal_code})` : (mrc?.full_name || route.internal_code);
                      return <RouteMetricRow key={route.id} name={name} m={m} view={view} />;
                    })}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function TmSummaryTab({ svGroups, weeks, weekNums, calcMetrics, period, vacations = [], servicePct = {}, personalForecasts = {} }) {
  const OVERLAP = new Set(['БЛ+МР','БЛ+ПМ','БЛ+ВЫХ','ОТ+МР','ОТ+ПМ','ОТ+ВЫХ','ДО+МР','ДО+ПМ','ДО+ВЫХ','ВК+МР','ВК+ПМ','ВК+ВЫХ']);
  const totals = {};
  weekNums.forEach(w => { totals[w] = { exits: 0, sick: 0, vacation: 0, dayoff: 0, overlaps: 0 }; });

  const allRoutesForSvc = svGroups.flatMap(g => g.routes.filter(r => !r.is_vacancy));
  const totalRoutesCount = allRoutesForSvc.length;

  svGroups.forEach(({ routes, statuses }) => {
    routes.forEach(r => {
      weekNums.forEach(w => {
        const m = calcMetrics(statuses, r.id, weeks[w]?.days||[]);
        totals[w].exits    += m.exits;
        totals[w].sick     += m.sick;
        totals[w].vacation += m.vacation;
        totals[w].dayoff   += m.dayoff;
        totals[w].overlaps += m.overlaps;
      });
    });
  });

  // Средний Service% по группе за неделю
  weekNums.forEach(w => {
    const pcts = allRoutesForSvc.map(r => servicePct[r.id]?.[w]).filter(p => p != null);
    totals[w].avgService = pcts.length ? (pcts.reduce((a,b)=>a+b,0) / pcts.length) : null;
  });

  // Пересечения отпусков по всей группе ТМ
  const allRoutes = svGroups.flatMap(g => g.routes.filter(r => !r.is_vacancy));
  const allRouteIds = allRoutes.map(r => r.id);
  const dayCounts = {};
  vacations.filter(v => allRouteIds.includes(v.route_id)).forEach(v => {
    const from = new Date(v.date_from), to = new Date(v.date_to);
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      const key = d.toISOString().split('T')[0];
      dayCounts[key] = (dayCounts[key] || 0) + 1;
    }
  });
  const overlapDaysCount = Object.values(dayCounts).filter(c => c >= 2).length;

  return (
    <div>
      {overlapDaysCount > 0 && (
        <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', borderRadius: 8, padding: '12px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--red)' }}>{overlapDaysCount}</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--red)' }}>дней с пересечением отпусков по группе</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>2 и более МРЧ одновременно в отпуске</div>
          </div>
        </div>
      )}

      <SectionTitle>Итоги по неделям — {period?.name}</SectionTitle>
      <Card>
        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {weekNums.map(w => {
              const c = WEEK_COLORS[w] || WEEK_COLORS[36];
              return (
                <div key={w} style={{ flex: 1, textAlign: 'center', padding: '12px 6px', borderRadius: 8, background: c.light }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: c.hdr }}>W{w}</div>
                  <div style={{ fontSize: 24, fontWeight: 700, margin: '4px 0' }}>{totals[w].exits}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>выходов</div>
                  <div style={{ fontSize: 10, color: 'var(--text-light)', marginTop: 1 }}>из {totalRoutesCount} марш.</div>
                  {totals[w].sick > 0 && <div style={{ fontSize: 10, color: 'var(--red)', marginTop: 4 }}>БЛ: {totals[w].sick}</div>}
                  {totals[w].vacation > 0 && <div style={{ fontSize: 10, color: 'var(--yellow)', marginTop: 2 }}>ОТ: {totals[w].vacation}</div>}
                  {totals[w].dayoff > 0 && <div style={{ fontSize: 10, color: '#843C0C', marginTop: 1 }}>ДО: {totals[w].dayoff}</div>}
                  {totals[w].overlaps > 0 && <div style={{ fontSize: 10, color: 'var(--green)', marginTop: 2 }}>↔: {totals[w].overlaps}</div>}
                  {totals[w].avgService != null && <div style={{ fontSize: 11, fontWeight: 700, color: c.hdr, marginTop: 4, borderTop: `1px solid ${c.hdr}30`, paddingTop: 4 }}>SL: {(totals[w].avgService*100).toFixed(0)}%</div>}
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      <DailyStatsPanel
        weeks={weeks}
        weekNums={weekNums}
        title="По дням — вся группа"
        getDayMetrics={(date) => {
          const acc = { exits: 0, sick: 0, vacation: 0, dayoff: 0, overlaps: 0 };
          svGroups.forEach(({ routes, statuses }) => {
            routes.filter(r => !r.is_vacancy).forEach(r => {
              const s = statuses[r.id]?.[date];
              if (!s) return;
              if (s==='1'||s==='СВ') acc.exits++;
              if (s==='БЛ-') acc.sick++;
              if (s==='ОТ-') acc.vacation++;
              if (s==='ДО-') acc.dayoff++;
              if (OVERLAP.has(s)) acc.overlaps++;
            });
          });
          return acc;
        }}
      />

      <SectionTitle>По каждому СВ</SectionTitle>
      {svGroups.map(({ sv, routes, statuses }) => (
        <Card key={sv.id}>
          <div style={{ padding: '10px 16px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{sv.full_name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{routes.length} маршрутов</div>
            </div>
          </div>
          <div style={{ padding: '10px 16px', display: 'flex', gap: 6 }}>
            {weekNums.map(w => {
              const m = routes.reduce((acc, r) => {
                const rm = calcMetrics(statuses, r.id, weeks[w]?.days||[]);
                acc.exits += rm.exits; acc.sick += rm.sick; acc.vacation += rm.vacation; acc.overlaps += rm.overlaps;
                return acc;
              }, { exits: 0, sick: 0, vacation: 0, overlaps: 0 });
              const c = WEEK_COLORS[w] || WEEK_COLORS[36];
              const svPcts = routes.filter(r => !r.is_vacancy).map(r => servicePct[r.id]?.[w]).filter(p => p != null);
              const svAvg = svPcts.length ? (svPcts.reduce((a,b)=>a+b,0) / svPcts.length) : null;
              const forecast = personalForecasts[sv.id]?.[w];
              return (
                <div key={w} style={{ flex: 1, textAlign: 'center', padding: '6px 4px', borderRadius: 6, background: c.light }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: c.hdr }}>W{w}</div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{m.exits}</div>
                  <div style={{ fontSize: 8, color: 'var(--text-light)' }}>из {routes.filter(r => !r.is_vacancy).length}</div>
                  {svAvg != null && <div style={{ fontSize: 9, fontWeight: 700, color: c.hdr, marginTop: 2 }}>SL:{(svAvg*100).toFixed(0)}%</div>}
                  {forecast != null && <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 1 }}>план:{forecast.toFixed(0)}%</div>}
                </div>
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
}

function TmDynamicsTab({ svGroups, weeks, weekNums, calcMetrics, vacations = [] }) {
  const compares = weekNums.slice(1).map((w, i) => [weekNums[i], w]);
  const GOOD = { exits: true, overlaps: true, sick: false, vacation: false, dayoff: false, vacancy: false, vacOverlap: false };
  const LABELS = { exits: 'Выходы', sick: 'БЛ', vacation: 'ОТ', overlaps: 'Перекр', vacOverlap: 'Дни пересеч. ОТ' };

  function overlapDaysInWeek(routeIds, weekDays) {
    const weekDateSet = new Set((weekDays || []).map(d => d.date));
    const dayCounts = {};
    vacations.filter(v => routeIds.includes(v.route_id)).forEach(v => {
      const from = new Date(v.date_from), to = new Date(v.date_to);
      for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
        const key = d.toISOString().split('T')[0];
        if (weekDateSet.has(key)) dayCounts[key] = (dayCounts[key] || 0) + 1;
      }
    });
    return Object.values(dayCounts).filter(c => c >= 2).length;
  }

  return (
    <div>
      <SectionTitle>Динамика неделя к неделе</SectionTitle>
      {svGroups.map(({ sv, routes, statuses }) => {
        const routeIds = routes.map(r => r.id);
        return (
        <Card key={sv.id}>
          <div style={{ padding: '10px 16px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 13 }}>{sv.full_name}</div>
          {compares.map(([wp, wc]) => {
            const prev = routes.reduce((acc, r) => { const m = calcMetrics(statuses, r.id, weeks[wp]?.days||[]); Object.keys(m).forEach(k => acc[k] = (acc[k]||0) + m[k]); return acc; }, { exits:0, sick:0, vacation:0, overlaps:0 });
            const curr = routes.reduce((acc, r) => { const m = calcMetrics(statuses, r.id, weeks[wc]?.days||[]); Object.keys(m).forEach(k => acc[k] = (acc[k]||0) + m[k]); return acc; }, { exits:0, sick:0, vacation:0, overlaps:0 });
            prev.vacOverlap = overlapDaysInWeek(routeIds, weeks[wp]?.days);
            curr.vacOverlap = overlapDaysInWeek(routeIds, weeks[wc]?.days);

            return (
              <div key={`${wp}-${wc}`} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', minWidth: 80 }}>W{wc} vs W{wp}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {Object.keys(LABELS).map(metric => {
                    const diff = curr[metric] - prev[metric];
                    const goodIfUp = GOOD[metric];
                    const isGood = goodIfUp ? diff > 0 : diff < 0;
                    const isBad  = goodIfUp ? diff < 0 : diff > 0;
                    const icon   = diff > 0 ? '▲' : diff < 0 ? '▼' : '=';
                    const color  = diff === 0 ? '#808080' : isGood ? '#276221' : '#9C0006';
                    const bg     = diff === 0 ? '#EDEDED' : isGood ? '#C6EFCE' : '#FFC7CE';
                    return (
                      <div key={metric} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4px 8px', borderRadius: 6, background: bg, minWidth: 44 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color }}>{icon}{Math.abs(diff) || ''}</span>
                        <span style={{ fontSize: 9, color, opacity: .8 }}>{LABELS[metric]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </Card>
        );
      })}
    </div>
  );
}

// ── DRILL-DOWN: ТМ РЕДАКТИРУЕТ КОНКРЕТНОГО СВ ─────────────────────────────────
function SvDrillDown({ svId, svGroups, vacations, period, weeks, weekNums, onBack, onUpdateGroups, onUpdateVacations, tmUserId, visitFacts = [], focus = null }) {
  const [canHide, setCanHide] = useState(false);
  const [hidKey, setHidKey] = useState(0);
  useEffect(() => { canHideRoutes(tmUserId).then(setCanHide); }, [tmUserId]);
  const group = svGroups.find(g => g.sv.id === svId);
  if (!group) return null;

  const svVacations = vacations.filter(v => group.routes.concat(group.pmRoutes || []).some(r => r.id === v.route_id));

  function updateGroupRoutes(updater) {
    onUpdateGroups(prev => prev.map(g => g.sv.id === svId ? { ...g, routes: updater(g.routes) } : g));
  }
  function updateGroupStatuses(routeId, date, code) {
    onUpdateGroups(prev => prev.map(g => {
      if (g.sv.id !== svId) return g;
      const next = { ...g.statuses };
      if (!next[routeId]) next[routeId] = {};
      if (code === null) delete next[routeId][date];
      else next[routeId] = { ...next[routeId], [date]: code };
      return { ...g, statuses: next };
    }));
  }

  return (
    <div>
      <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--accent)', fontSize: 13, fontWeight: 600, marginBottom: 12, cursor: 'pointer' }}>
        ← Назад к группе
      </button>
      <SectionTitle>{group.sv.full_name}</SectionTitle>

      <SvManage sv={group.sv} actorId={tmUserId}
        onChanged={async () => {
          const { data } = await supabase.from('supervisors').select('id, full_name, internal_code, user_id').eq('id', svId).maybeSingle();
          if (data) onUpdateGroups(prev => prev.map(g => g.sv.id === svId ? { ...g, sv: { ...g.sv, ...data } } : g));
        }} />

      <EditableTabell
        pmRoutes={group.pmRoutes || []}
        onHideRoute={canHide ? async (route) => {
          await hideRoute(route.id, true, tmUserId);
          updateGroupRoutes(rs => rs.filter(r => r.id !== route.id));
          setHidKey(x => x + 1);
        } : null}
        period={period}
        weeks={weeks}
        weekNums={weekNums}
        routes={group.routes}
        statuses={group.statuses}
        vacations={svVacations}
        visitFacts={visitFacts.filter(vf => group.routes.some(r => r.id === vf.route_id))}
        focus={focus}
        onSetStatus={async ({ routeId, date, code }) => {
          if (code === null) await clearStatus({ periodId: period.id, routeId, date });
          else await setStatus({ periodId: period.id, routeId, date, statusCode: code, userId: tmUserId });
        }}
        onLocalStatusChange={(routeId, date, code) => updateGroupStatuses(routeId, date, code)}
        onSetVacation={async ({ routeId, dateFrom, dateTo, workingDays }) => {
          await setVacation({ periodId: period.id, routeId, dateFrom, dateTo, workingDays, userId: tmUserId, isTmAction: true });
        }}
        onLocalVacationChange={(routeId, vac) => {
          onUpdateVacations(prev => {
            const filtered = prev.filter(v => v.route_id !== routeId);
            return vac ? [...filtered, { route_id: routeId, status: 'approved', ...vac }] : filtered;
          });
        }}
        onDeleteVacation={async (routeId) => {
          await deleteVacation(period.id, routeId);
        }}
        onCloseVacancy={async (routeId, fullName) => {
          await closeVacancyByRoute(routeId, fullName);
        }}
        onApprove={async (vacationId) => {
          const { approveVacation } = await import('../lib/data');
          const updated = await approveVacation(vacationId, tmUserId);
          onUpdateVacations(prev => prev.map(v => v.id === vacationId ? updated : v));
        }}
        onReject={async (vacationId) => {
          const { rejectVacation } = await import('../lib/data');
          const updated = await rejectVacation(vacationId, tmUserId);
          onUpdateVacations(prev => prev.map(v => v.id === vacationId ? updated : v));
        }}
        onLocalRouteChange={(routeId, patch) => {
          updateGroupRoutes(routes => routes.map(r => r.id === routeId ? { ...r, ...patch } : r));
        }}
      />
      {canHide && <HiddenRoutes svId={svId} actorId={tmUserId} refreshKey={hidKey}
        onRestored={() => window.location.reload()} />}
    </div>
  );
}
